import { computed, inject, Injectable } from '@angular/core';
import { UpsellingRequest } from '../../models/upselling-request';
import { FilterObject } from '../../models/filter-object';
import { FlightState } from './flight.state';
import { FlightService } from './flight.service';
import { SweetAlertService } from '../../../../core/services/sweet-alert.service';
import { Observable, forkJoin, map } from 'rxjs';
import { Airport } from '../../models/search-airport';
import { SearchFilterObject } from '../../models/search-filter-object';
import { SortType } from '../../types/sort-type';

@Injectable({
  providedIn: 'root',
})
export class FlightFacade {
  private flightService = inject(FlightService);
  private flightState = inject(FlightState);
  private sweetAlert = inject(SweetAlertService);

  constructor() {
    this.initResearchSubscription();

    this.flightService.researchError$.subscribe(() => {
      this.flightState.flightSearchError.set(true);
      this.flightState.flightSearchLoading.set(false);
      this.flightState.flightReSearchLoading.set(false);
    });
  }

  initResearchSubscription() {
    this.flightService.researchFlights$.subscribe({
      next: (res) => {
        this.flightState.setflightResponse(res);
        const isFinished = res?.response?.responsePercentage === '100.0%';
        const hasFlights = !!res?.response?.flights?.length;

        if (isFinished || hasFlights) {
          this.flightState.flightSearchLoading.set(false);
          this.flightState.flightReSearchLoading.set(false);
        }
      },
    });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////

  loadAirportObjects(searchData: any): void {
    const airportCodes = [
      ...new Set(
        searchData.formValue.trips.flatMap((trip: any) => [
          trip.departureAirport.iata.toUpperCase(),
          trip.arrivalAirport.iata.toUpperCase(),
        ])
      ),
    ];

    const requests = airportCodes.map((code: any) =>
      this.flightService.searchAirports(code).pipe(
        map((airports: Airport[]) => {
          const airport = airports.find((x) => x.iata.toUpperCase() === code);

          if (!airport) {
            return {} as Airport;
          }

          return airport;
        })
      )
    );

    forkJoin(requests).subscribe({
      next: (airports: Airport[]) => {
        const airportMap = new Map<string, Airport>();

        airports.forEach((airport) => {
          airportMap.set(airport.iata.toUpperCase(), airport);
        });

        searchData.formValue.trips.forEach((trip: any) => {
          trip.departureAirport = airportMap.get(
            trip.departureAirport.iata.toUpperCase()
          );

          trip.arrivalAirport = airportMap.get(
            trip.arrivalAirport.iata.toUpperCase()
          );
        });

        this.searchFlights(searchData);
      },

      error: (err) => {
        console.error(err);
      },
    });
  }

  searchAirports(query: string) {
    this.flightService.searchAirports(query).subscribe({
      next: (res) => {
        this.flightState.airports.set(res);
      },
      error: (err) => {
        this.flightState.airports.set([]);
        // offline: the global network-error screen (jwt.interceptor) handles it
        if (err?.status === 0 && !navigator.onLine) return;
        // status 0 carries a ProgressEvent, not a message from the BE: show the generic one
        this.sweetAlert.sweetErrors('Error', err?.status === 0 ? null : err);
      },
    });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////

  searchFlights(criteria: any) {
    this.flightState.flightSearchLoading.set(true);
    this.flightState.searchCriteria.set(criteria);
    // a new search invalidates the previous one: upselling offers are keyed by flightNumber,
    // which repeats across searches, and a price error must not follow the user to the new flight
    this.flightState.upsellingResponses.set({});
    this.flightState.flightError.set(false);
    this.flightState.flightSearchError.set(false);
    // filters and the page belong to the previous search (price bounds, airlines); the sort is kept
    this.flightState.savedFilters.set(null);
    this.flightState.flightFilters.set(null);
    this.flightState.currentPage.set(1);

    sessionStorage.setItem('searchCriteria', JSON.stringify(criteria));

    this.flightService
      .searchFlights(criteria, {
        pageNumber: 1,
        pageSize: 10,
        sortType: this.flightState.sortType(),
      })
      .subscribe({
      next: (response) => {
        this.flightState.setflightResponse(response);
        this.flightState.flightResponseSearchId.set(
          response?.response?.searchId
        );
        sessionStorage.setItem('flightSearchId', response?.response?.searchId);

        // the stops chosen in the search are the Stops filter: from the results-page search (pendingStops),
        // or from the home search / a link ("Direct" = no connection, "1 stop" = at most one)
        // the same search run again keeps its filters
        const kept = this.flightState.pendingFilters();
        this.flightState.pendingFilters.set(null);
        const max = criteria?.formValue?.maxNumberOfConnections;
        const stops =
          this.flightState.pendingStops() ??
          (max === 0 || max === '0' ? [0] : max === 1 || max === '1' ? [0, 1] : null);
        this.flightState.pendingStops.set(null);
        if (kept) {
          this.applyFilters(kept);
        } else if (stops?.length) {
          this.applyFilters({ selectedStops: stops });
        }

        setTimeout(() => {
          this.flightState.flightSearchLoading.set(false);
        }, 2000);
      },
      error: () => {
        this.flightState.flightSearchLoading.set(false);
      },
    });
  }

  researchFlights(params?: FilterObject) {
    if (params) this.flightState.flightFilters.set(params);

    this.flightState.flightReSearchLoading.set(true);
    this.flightState.flightSearchError.set(false);

    const searchId = computed(() => this.flightState.flightResponseSearchId());
    const pageNumber = computed(() => this.flightState.currentPage());
    const tripType = this.flightState.searchCriteria()?.tripType;
    const filters = computed(() => this.flightState.flightFilters());

    const queryParams: FilterObject = {
      ...filters(),
      pageNumber: pageNumber(),
      pageSize: 10,
      sortType: this.flightState.sortType(),
    };

    this.flightService.triggerResearch(searchId() || '', tripType, queryParams);
  }

  changePage(page: number) {
    this.flightState.currentPage.set(page);
    this.researchFlights();
  }

  /**
   * Filters of the results page; null removes them all. The list starts again from page 1.
   * Stops and airlines: missing = every option ticked (no filter); an empty list = every option unticked,
   * which matches no flight — the list is emptied here, without asking the backend.
   */
  applyFilters(filters: SearchFilterObject | null) {
    const clean: SearchFilterObject = { ...(filters ?? {}) };

    (Object.keys(clean) as (keyof SearchFilterObject)[]).forEach((key) => {
      const value = clean[key];
      const noneTicked =
        (key === 'selectedStops' || key === 'selectedAirlines') && Array.isArray(value);
      if (value == null || (Array.isArray(value) && !value.length && !noneTicked)) {
        delete clean[key];
      }
    });

    const hasFilters = Object.keys(clean).length > 0;

    this.flightState.savedFilters.set(hasFilters ? clean : null);
    this.flightState.currentPage.set(1);

    if (clean.selectedStops?.length === 0 || clean.selectedAirlines?.length === 0) {
      const current = this.flightState.flightResponse();
      if (current) {
        this.flightState.setflightResponse({
          ...current,
          totalRecords: 0,
          pageNumber: 1,
          response: { ...current.response, flights: [] },
        });
      }
      return;
    }

    this.researchFlights(hasFilters ? clean : {});
  }

  /** Best / Cheapest / Fastest: the backend sorts, the list starts again from page 1. */
  changeSort(sortType: SortType) {
    if (sortType === this.flightState.sortType()) return;

    this.flightState.sortType.set(sortType);
    this.flightState.currentPage.set(1);
    this.researchFlights();
  }

  /** "Search again" after the progressive search stopped. */
  /** the same search again (Search again, Refresh results): the sort and the filters are kept */
  retrySearch() {
    const criteria = this.flightState.searchCriteria();
    if (!criteria) return;
    this.flightState.pendingFilters.set(this.flightState.savedFilters());
    this.searchFlights(criteria);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////

  upselling(upsellingRequest: UpsellingRequest) {
    this.flightState.upsellingLoading.set(true);

    this.flightService.getUpselling(upsellingRequest).subscribe({
      next: (response) => {
        this.flightState.upsellingLoading.set(false);
        this.flightState.upsellingResponses.update((state) => ({
          ...state,
          [upsellingRequest.flightNumber]: response,
        }));
      },
      error: (error) => {
        this.flightState.upsellingLoading.set(false);
        this.flightState.upsellingError.set(true);
        this.sweetAlert.sweetErrors('Error', error);
      },
    });
  }

  confirmPrice(upsellingRequest: UpsellingRequest, bags: boolean = true , meals:boolean = true) {
    this.flightState.flightDetailsLoading.set(true);
    this.flightState.flightError.set(false);
    this.flightState.flightDetailsResponse.set(null);

    return this.flightService.confirmPrice(upsellingRequest, bags , meals).subscribe({
      next: (response) => {
        let orderItems = [
          {
            searchId: response.searchId,
            type: 'FLIGHT_RESERVATION',
            departureCity:
              response.flights[0]?.itineraries[0]?.departureAirport,
            arrivalCity: response.flights[0]?.itineraries[0]?.arrivalAirport,
            flightNumber: response.flights[0]?.flightNumber || 0,
          },
        ];
        this.flightState.orderItems.set(orderItems);
        this.flightState.flightDetailsLoading.set(false);
        this.flightState.flightDetailsResponse.set(response);
        sessionStorage.setItem('upsellingCriteria', JSON.stringify(response));
        this.flightState.upsellingCriteria.set(response);
      },
      error: (error) => {
        // status 0 = no connection: the global network-error screen (jwt.interceptor) handles it
        // and its retry reloads the page; the time-out screen is only for an error from the BE
        if (error?.status === 0) return;

        this.flightState.flightError.set(true);
        this.flightState.flightDetailsLoading.set(false);
        this.sweetAlert.sweetErrors('Error', error);
      },
    });
  }
}
